import React, { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const treatments = [
  {
    title: "Pet Vaccination",
    description: "Timely vaccinations for complete protection against severe infectious diseases.",
    icon: "/images/treat_vaccination.png",
    path: "/vaccination",
    accent: "from-emerald-50 to-green-100",
    dot: "bg-primary",
    num: "01"
  },
  {
    title: "Expert Surgery",
    description: "Professional bone and soft tissue surgeries using advanced anaesthesia.",
    icon: "/images/treat_surgery.png",
    path: "/surgeries",
    accent: "from-amber-50 to-orange-100",
    dot: "bg-amber-500",
    num: "02"
  },
  {
    title: "Diagnostic Tests",
    description: "Accurate blood tests, hematology, and rapid lab investigations.",
    icon: "/images/treat_diagnostics.png",
    path: "/diagnostics",
    accent: "from-blue-50 to-indigo-100",
    dot: "bg-blue-500",
    num: "03"
  },
  {
    title: "Health Monitoring",
    description: "Regular checkups and vital tracking for your pet's ongoing wellbeing.",
    icon: "/images/treat_monitoring.png",
    path: "/monitoring",
    accent: "from-teal-50 to-cyan-100",
    dot: "bg-teal-500",
    num: "04"
  },
  {
    title: "Clean Procedures",
    description: "Highly hygienic and safe surgical environments with strict sterilization.",
    icon: "/images/treat_clean.png",
    path: "/sterilization",
    accent: "from-violet-50 to-purple-100",
    dot: "bg-violet-500",
    num: "05"
  },
  {
    title: "Internal Care",
    description: "Modern treatments for complex internal health issues and oncology.",
    icon: "/images/treat_internal.png",
    path: "/internal-medicine",
    accent: "from-rose-50 to-pink-100",
    dot: "bg-rose-500",
    num: "06"
  }
];

const Treatments = () => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.treat-header',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.1, duration: 1, ease: 'power3.out',
          scrollTrigger: { trigger: '.treat-header', start: 'top 85%' }
        }
      );
      gsap.fromTo('.treat-card',
        { y: 60, opacity: 0, scale: 0.96 },
        { y: 0, opacity: 1, scale: 1, stagger: 0.1, duration: 0.9, ease: 'power3.out',
          scrollTrigger: { trigger: '.treat-grid', start: 'top 80%' }
        }
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="py-24 md:py-32 bg-[#f8faff] border-y border-navy/5 relative overflow-hidden" id="treatments">

      {/* background orbs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/4 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-400/4 rounded-full blur-[150px] pointer-events-none" />

      <div className="section-container relative z-10">

        {/* ── Header ── */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-24 items-end mb-16 md:mb-20">
          <div className="space-y-5">
            <div className="treat-header inline-flex items-center gap-3">
              <span className="w-10 h-px bg-primary/40" />
              <span className="text-primary font-black tracking-[0.25em] uppercase text-[10px]">Our Specialities</span>
            </div>
            <h2 className="treat-header text-navy font-bold leading-[0.92] tracking-tight text-[clamp(2.5rem,7vw,5rem)]">
              Advanced <em className="font-normal text-primary not-italic">Veterinary</em><br />Medical Care
            </h2>
          </div>
          <p className="treat-header text-muted-foreground text-lg leading-relaxed max-w-md lg:pb-2">
            Professional pet care and surgical services provided in a clean, hygienic, and highly-equipped clinical environment.
          </p>
        </div>

        {/* ── Cards Grid ── */}
        <div className="treat-grid grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {treatments.map((item, i) => (
            <Link to={item.path} key={i}
              className="treat-card group relative bg-white border border-navy/6 rounded-[2.5rem] overflow-hidden hover:shadow-[0_25px_60px_rgba(0,0,20,0.1)] hover:-translate-y-2 transition-all duration-700 flex flex-col">

              {/* Top accent strip */}
              <div className={`h-1 w-full bg-gradient-to-r ${item.accent.replace('from-', 'from-').replace('50', '400').replace('100', '500')}`} />

              {/* Atmospheric glow */}
              <div className={`absolute -top-16 -right-16 w-52 h-52 ${item.accent.includes('emerald') ? 'bg-primary/10' : item.accent.includes('amber') ? 'bg-amber-400/10' : item.accent.includes('blue') ? 'bg-blue-400/10' : item.accent.includes('teal') ? 'bg-teal-400/10' : item.accent.includes('violet') ? 'bg-violet-400/10' : 'bg-rose-400/10'} rounded-full blur-[80px] pointer-events-none opacity-70 group-hover:opacity-100 transition-opacity duration-700`} />

              <div className="relative z-10 p-8 md:p-10 flex flex-col flex-1">

                {/* ── Top row: image + number ── */}
                <div className="flex items-start justify-between mb-7">
                  <div className={`w-[72px] h-[72px] rounded-2xl bg-gradient-to-br ${item.accent} flex items-center justify-center shadow-sm overflow-hidden group-hover:scale-105 group-hover:-rotate-3 transition-all duration-500`}>
                    <img
                      src={item.icon}
                      alt={item.title}
                      className="w-full h-full object-contain p-2"
                    />
                  </div>
                  <span className="text-[11px] font-black tracking-widest text-navy/15 group-hover:text-primary/30 transition-colors duration-500 tabular-nums">{item.num}</span>
                </div>

                {/* ── Title & desc ── */}
                <div className="flex-1 space-y-3 mb-8">
                  <h3 className="font-bold text-navy text-[1.2rem] leading-tight group-hover:text-primary transition-colors duration-500">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                </div>

                {/* ── Footer CTA ── */}
                <div className="mt-auto pt-5 border-t border-navy/6 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${item.dot}`} />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground group-hover:text-primary transition-colors duration-500">Learn More</span>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-500 shadow-md group-hover:shadow-[0_8px_24px_rgba(3,137,3,0.35)]">
                    <ArrowUpRight size={16} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Treatments;
